/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Box, Typography } from '@mui/material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import { toast } from 'react-toastify';

const EMPTY_TYPE = { name: '', description: '' };

/**
 * Compartment types — the kinds of space a project can need (Controlled
 * Environment, Field Plot, Animal Facility …). Compartments and facilities
 * are tagged with them, and the application form asks for one first so it can
 * offer only the facilities that have that kind of space.
 */
const CompartmentTypeManagement = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // One dialog for both create and edit: `editing._id` is set when editing.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const filteredTypes = types.filter(t => searchTerm === '' ||
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase()));

  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredTypes,
    { defaultSortColumn: 'name', defaultSortOrder: 'asc' }
  );

  const fetchTypes = async () => {
    try {
      const response = await axios.get('/api/compartment-types');
      setTypes(response.data);
    } catch (err) {
      setError('Failed to fetch compartment types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleSave = async () => {
    if (!editing.name?.trim()) {
      toast.error('Type is required');
      return;
    }
    setSaving(true);
    try {
      if (editing._id) {
        await axios.put(`/api/compartment-types/${editing._id}`, editing);
        toast.success('Compartment type updated');
      } else {
        await axios.post('/api/compartment-types', editing);
        toast.success('Compartment type created');
      }
      setEditing(null);
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save compartment type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`Delete the compartment type "${type.name}"?`)) return;
    try {
      await axios.delete(`/api/compartment-types/${type._id}`);
      toast.success('Compartment type deleted');
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete compartment type');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Box sx={{ p: '20px' }}>
      <h2>Compartment Type Management</h2>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        The kinds of space a project can need. Tag compartments and facilities with these
        types, and the application form will ask for the project's type first and then offer
        only the facilities that have that kind of space.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          label="Search Compartment Types"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by type or description..."
          sx={{ minWidth: 300 }}
        />
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button variant="contained" color="primary" onClick={() => setEditing({ ...EMPTY_TYPE })}>
          Add New Compartment Type
        </Button>
        <Button variant="outlined" onClick={resetSort}>
          Reset Sort
        </Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader
              column="name"
              label="Type"
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <SortableTableHeader
              column="description"
              label="Description"
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={3} style={{ textAlign: 'center', color: '#666' }}>
                No compartment types yet.
              </td>
            </tr>
          ) : sortedData.map(type => (
            <tr key={type._id}>
              <td>{type.name}</td>
              <td>{type.description || '—'}</td>
              <td className="action-buttons">
                <Button
                  variant="contained"
                  size="small"
                  className="row-action-edit"
                  style={{ marginRight: '8px' }}
                  onClick={() => setEditing({ ...type })}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDelete(type)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?._id ? 'Edit Compartment Type' : 'Create New Compartment Type'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Type"
            fullWidth
            margin="normal"
            required
            value={editing?.name || ''}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            placeholder="e.g., Controlled Environment"
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            minRows={2}
            value={editing?.description || ''}
            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
            {editing?._id ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompartmentTypeManagement;
